export interface ReloadGuardOptions {
    /** Whether `navigator.serviceWorker.controller` was non-null when the page loaded. */
    hadController: boolean;
    reload: () => void;
    /** When true at the moment a reload is due (e.g. a sign-in is in flight), the reload is deferred via `onIdle`. */
    isBusy?: () => boolean;
    /**
     * When true, a reload this tab did not ask for is deferred via `onIdle` as well: the page holds something a reload
     * would throw away (see utils/reload-blockers.ts). A member's own "Update now" is not held back by it.
     */
    isBlocked?: () => boolean;
    /**
     * Registers `resume`, to be called once `isDeferred()` turns false. Called at most once per guard. `isDeferred`
     * combines `isBusy` and `isBlocked`, so a reactive caller can watch it directly.
     */
    onIdle?: ( resume: () => void, isDeferred: () => boolean ) => void;
}

/**
 * Reload EVERY client when a new service worker takes control, not just the tab that clicked "Update now":
 * a second window (or the standalone PWA window) would otherwise keep executing the old bundle against a
 * database the new bundle has since upgraded. The first install (no prior controller) must not reload
 * unless the user asked, and a second `controllerchange` must not reload twice. A reload that lands while
 * `isBusy()` (or, for a reload the tab did not ask for, `isBlocked()`) is deferred to `onIdle` — still at most
 * one reload in total.
 */
export function createReloadGuard( { hadController, reload, isBusy, isBlocked, onIdle }: ReloadGuardOptions ) {
    let userRequested = false;
    let refreshing = false;
    let reloaded = false;
    const reloadOnce = (): void => {
        if ( reloaded ) return;
        reloaded = true;
        reload();
    };
    const isDeferred = (): boolean => !!isBusy?.() || ( !userRequested && !!isBlocked?.() );
    return {
        userRequestedUpdate(): void {
            userRequested = true;
            // The new worker already took control while a blocker held this tab back: the member's own
            // "Update now" brings no second controllerchange, so it has to release the held reload itself.
            if ( refreshing && !isDeferred() ) {
                reloadOnce();
            }
        },
        onControllerChange(): void {
            if ( refreshing || !( hadController || userRequested ) ) return;
            refreshing = true;
            if ( isDeferred() && onIdle ) {
                onIdle( reloadOnce, isDeferred );
                return;
            }
            reloadOnce();
        }
    };
}
